import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings, Brain, Volume2, CheckCircle, AlertCircle, ChevronRight
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { isUserCoach } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { fetchCoachConfig, saveCoachConfig } from '../../../lib/ai-coach';
import { fetchGhlSettings, saveGhlSettings } from '../../../lib/ghl';

export function AICoachSettingsTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [elevenlabsApiKey, setElevenlabsApiKey] = useState('');
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [showElevenlabsKey, setShowElevenlabsKey] = useState(false);
  const [hasElevenlabsKey, setHasElevenlabsKey] = useState(false);
  const [replaceElevenlabsKey, setReplaceElevenlabsKey] = useState(false);
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [cloneName, setCloneName] = useState('');

  const [ghlLocationId, setGhlLocationId] = useState('');
  const [ghlPipelineId, setGhlPipelineId] = useState('');
  const [ghlPipelineStageId, setGhlPipelineStageId] = useState('');
  const [ghlCustomToken, setGhlCustomToken] = useState('');
  const [showGhlToken, setShowGhlToken] = useState(false);
  const [hasGhlCustomToken, setHasGhlCustomToken] = useState(false);
  const [replaceGhlToken, setReplaceGhlToken] = useState(false);
  const [isSavingGhl, setIsSavingGhl] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/tr/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coachRecord) { navigate('/tr/login'); return; }
        setCoachId(coachRecord.id);

        const config = await fetchCoachConfig(coachRecord.id);
        if (config) {
          setElevenlabsAgentId(config.elevenlabsAgentId);
          setIsEnabled(config.isEnabled);
          setHasElevenlabsKey(config.hasElevenlabsApiKey);
          setSetupCompleted(config.setupCompleted);
          setCloneName(config.cloneName);
        }
        const ghl = await fetchGhlSettings(coachRecord.id);
        if (ghl) {
          setGhlLocationId(ghl.locationId);
          setGhlPipelineId(ghl.pipelineId);
          setGhlPipelineStageId(ghl.pipelineStageId);
          setHasGhlCustomToken(ghl.hasCustomToken);
        }
      } catch (err) {
        console.error('Error loading AI Coach settings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleSaveGhl = async () => {
    if (!coachId) return;
    setIsSavingGhl(true);
    setToast(null);

    try {
      await saveGhlSettings(coachId, {
        locationId: ghlLocationId,
        pipelineId: ghlPipelineId,
        pipelineStageId: ghlPipelineStageId,
        customToken: replaceGhlToken ? ghlCustomToken : undefined,
      });
      if (ghlCustomToken.trim().length > 0) {
        setHasGhlCustomToken(true);
        setGhlCustomToken('');
        setReplaceGhlToken(false);
      }
      setToast({ type: 'success', message: 'Go High Level ayarlari kaydedildi' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'GHL ayarlari kaydedilemedi' });
    } finally {
      setIsSavingGhl(false);
    }
  };

  const handleSave = async () => {
    if (!coachId) return;
    setIsSaving(true);
    setToast(null);

    try {
      await saveCoachConfig(coachId, {
        openaiApiKey: '',
        openaiAssistantId: '',
        elevenlabsApiKey,
        elevenlabsAgentId,
        isEnabled,
      });
      if (elevenlabsApiKey.trim().length > 0) {
        setHasElevenlabsKey(true);
        setElevenlabsApiKey('');
        setReplaceElevenlabsKey(false);
      }
      setToast({ type: 'success', message: 'Ayarlar basariyla kaydedildi' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Ayarlar kaydedilemedi' });
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="YZ Koc ayarlari yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">YZ Koc Ayarlari</h1>
              <p className="freud-text-sm text-chocolate-500">YZ kocluk asistanini yapilandirin</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-4 rounded-xl ${
              toast.type === 'success'
                ? 'bg-sage-100 text-sage-700'
                : 'bg-warm-100 text-warm-700'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm font-medium">{toast.message}</p>
          </motion.div>
        )}

        {/* YZ Koc Klonu Kurulumu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sage-100 rounded-lg">
                <Brain className="w-5 h-5 text-sage-600" />
              </div>
              <div>
                <h2 className="freud-heading-base text-chocolate-900">
                  YZ Koc Klonu {cloneName ? `- ${cloneName}` : ''}
                </h2>
                <p className="freud-text-sm text-chocolate-500">
                  {setupCompleted
                    ? 'YZ Koc Klonunuz yapilandirildi. Degisiklik yapmak icin kurulumu duzenleyin.'
                    : 'YZ Koc Klonunuzu tarzi, metodolojisi ve bilgi tabaniyla kurun.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/tr/coach/ai-setup')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-all"
            >
              {setupCompleted ? 'Kurulumu Duzenle' : 'Kurulumu Baslat'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {setupCompleted && (
            <div className="mt-4 flex items-center gap-2 text-sm text-sage-600">
              <CheckCircle className="w-4 h-4" />
              <span>Kurulum tamamlandi - YZ Koc Klonu hazir</span>
            </div>
          )}
        </motion.div>

        {/* ElevenLabs Ses Yapilandirmasi */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-warm-100 rounded-lg">
              <Volume2 className="w-5 h-5 text-warm-600" />
            </div>
            <div>
              <h2 className="freud-heading-base text-chocolate-900">ElevenLabs Ses Yapilandirmasi</h2>
              <p className="freud-text-sm text-chocolate-500">Istege bagli: Ses tabanli YZ koclugunu etkinlestirin</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                ElevenLabs API Anahtari
              </label>
              {hasElevenlabsKey && !replaceElevenlabsKey ? (
                <div className="flex items-center justify-between rounded-xl border border-sage-200 bg-sage-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-600" />
                    <span className="text-sm text-sage-700 font-medium">Anahtar yapilandirildi (sifrelendi)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplaceElevenlabsKey(true)}
                    className="text-xs font-medium text-sage-700 hover:text-sage-900 underline"
                  >
                    Degistir
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type={showElevenlabsKey ? 'text' : 'password'}
                      value={elevenlabsApiKey}
                      onChange={(e) => setElevenlabsApiKey(e.target.value)}
                      placeholder="ElevenLabs API anahtarinizi girin"
                      className="freud-input pr-20 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowElevenlabsKey(!showElevenlabsKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-chocolate-400 hover:text-chocolate-600"
                    >
                      {showElevenlabsKey ? 'Gizle' : 'Goster'}
                    </button>
                  </div>
                  {hasElevenlabsKey && (
                    <button
                      type="button"
                      onClick={() => { setReplaceElevenlabsKey(false); setElevenlabsApiKey(''); }}
                      className="text-xs text-chocolate-500 hover:text-chocolate-700 mt-1"
                    >
                      Degistirmeyi iptal et
                    </button>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                ElevenLabs Ajan ID
              </label>
              <input
                type="text"
                value={elevenlabsAgentId}
                onChange={(e) => setElevenlabsAgentId(e.target.value)}
                placeholder="ElevenLabs Ajan ID'nizi girin"
                className="freud-input font-mono text-sm"
              />
            </div>

            <div className="mt-4 p-4 bg-cream-100 rounded-xl border border-cream-200">
              <p className="text-sm font-medium text-chocolate-700 mb-2">Dinamik Degiskenler</p>
              <p className="text-xs text-chocolate-500 mb-3">
                Her ses oturumunun basinda katilimci verilerini eklemek icin ElevenLabs ajan komutunuzda bu yer tutucularini kullanin:
              </p>
              <div className="space-y-1.5">
                {[
                  ['{{respondent_name}}', 'Katilimcinin tam adi'],
                  ['{{assessment_results}}', 'Tum ozellik puanlari (orn. Hirsi: %75)'],
                  ['{{harmonic_results}}', 'Tum harmonik olcek puanlari'],
                  ['{{dominant_harmonic}}', 'Yuzde ile en yuksek harmonik seviye'],
                  ['{{top_characteristics}}', 'Aciklamalariyla birlikte en yuksek 3 ozellik'],
                  ['{{ai_analysis}}', 'Son sonuctan YZ anlatim analizi'],
                  ['{{pathway_context}}', 'Aktif yol haritasi ve asama ilerlemesi'],
                  ['{{coach_notes}}', 'Bu katilimci hakkindaki ozel notlariniz (dahil edildiginde)'],
                  ['{{respondent_brief}}', 'Yukaridaki tum bolumleri birlestiren tam yapilandirilmis ozet'],
                ].map(([token, desc]) => (
                  <div key={token} className="flex items-start gap-2">
                    <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-cream-300 text-chocolate-700 font-mono flex-shrink-0">{token}</code>
                    <span className="text-xs text-chocolate-500">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Go High Level Entegrasyonu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="freud-heading-base text-chocolate-900">Go High Level Entegrasyonu</h2>
                {ghlLocationId.trim() ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-sage-700 bg-sage-100 px-2 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3" /> Baglandi
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-chocolate-500 bg-cream-100 px-2 py-0.5 rounded-full">
                    Yapilandirilmadi
                  </span>
                )}
              </div>
              <p className="freud-text-sm text-chocolate-500">
                Degerlendirme ve magnet sayfasi kayitlarini GHL hesabiniza otomatik gonderin
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Konum ID <span className="text-chocolate-400 font-normal">(gerekli)</span>
              </label>
              <input
                type="text"
                value={ghlLocationId}
                onChange={(e) => setGhlLocationId(e.target.value)}
                placeholder="orn. ve9EPM428h8vShlRW1KT"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                GHL alt hesap URL'nizde veya Ayarlar &rarr; Is Bilgileri'nde bulunur
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Boru Hatti ID
              </label>
              <input
                type="text"
                value={ghlPipelineId}
                onChange={(e) => setGhlPipelineId(e.target.value)}
                placeholder="orn. abc123def456"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                GHL &rarr; Firsatlar &rarr; Boru Hatlari &rarr; boru hattini secin &rarr; ID'yi URL'den kopyalayin
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Boru Hatti Asama ID <span className="text-chocolate-400 font-normal">(yeni adaylar icin)</span>
              </label>
              <input
                type="text"
                value={ghlPipelineStageId}
                onChange={(e) => setGhlPipelineStageId(e.target.value)}
                placeholder="orn. stage_xyz789"
                className="freud-input font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Ozel GHL Token{' '}
                <span className="text-chocolate-400 font-normal">
                  (kendi GHL hesabiniz varsa)
                </span>
              </label>
              {hasGhlCustomToken && !replaceGhlToken ? (
                <div className="flex items-center justify-between rounded-xl border border-sage-200 bg-sage-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-600" />
                    <span className="text-sm text-sage-700 font-medium">Token yapilandirildi (sifrelendi)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplaceGhlToken(true)}
                    className="text-xs font-medium text-sage-700 hover:text-sage-900 underline"
                  >
                    Degistir
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type={showGhlToken ? 'text' : 'password'}
                      value={ghlCustomToken}
                      onChange={(e) => setGhlCustomToken(e.target.value)}
                      placeholder="GHL Ozel Entegrasyon tokeninizi yapistirin"
                      className="freud-input pr-20 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGhlToken(!showGhlToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-chocolate-400 hover:text-chocolate-600"
                    >
                      {showGhlToken ? 'Gizle' : 'Goster'}
                    </button>
                  </div>
                  {hasGhlCustomToken && (
                    <button
                      type="button"
                      onClick={() => { setReplaceGhlToken(false); setGhlCustomToken(''); }}
                      className="text-xs text-chocolate-500 hover:text-chocolate-700 mt-1"
                    >
                      Degistirmeyi iptal et
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveGhl}
                disabled={isSavingGhl}
                className="freud-btn-primary px-6 py-2.5 disabled:opacity-60 text-sm"
              >
                {isSavingGhl ? 'Kaydediliyor...' : 'GHL Ayarlarini Kaydet'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* YZ Kocu Etkinlestir */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cream-100 rounded-lg">
                <Brain className="w-5 h-5 text-chocolate-600" />
              </div>
              <div>
                <h2 className="freud-heading-base text-chocolate-900">YZ Kocu Etkinlestir</h2>
                <p className="freud-text-sm text-chocolate-500">
                  {setupCompleted
                    ? 'Katilimcilariniz icin YZ Kocunu kullanilabilir hale getirmek icin degistirin'
                    : 'Etkinlestirmek icin once kurulum sihirbazini tamamlayin'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setupCompleted && setIsEnabled(!isEnabled)}
              disabled={!setupCompleted}
              className="group flex items-center gap-2"
            >
              <div
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  isEnabled ? 'bg-sage-500' : 'bg-gray-300'
                } ${!setupCompleted ? 'opacity-40' : ''}`}
              >
                <div
                  className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                    isEnabled ? 'translate-x-[30px]' : 'translate-x-0.5'
                  }`}
                />
              </div>
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-end"
        >
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="freud-btn-primary px-8 py-3 disabled:opacity-60"
          >
            {isSaving ? 'Kaydediliyor...' : 'Ayarlari Kaydet'}
          </button>
        </motion.div>
      </main>
    </div>
  );
}
